import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Bell, Save, ListPlus, QrCode as QrIcon, Download, Copy } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = (token) => ({ Authorization: `Bearer ${token}` });

// ---------- Communicate tab ----------
export function AdminCommunicate({ token }) {
  const [target, setTarget] = useState("all");
  const [userId, setUserId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [channels, setChannels] = useState({ inapp: true, whatsapp: true, email: false });
  const [busy, setBusy] = useState(false);
  const [waLinks, setWaLinks] = useState([]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject & body required"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/broadcast`, {
        target, user_id: userId || null, subject, body,
        channels: Object.keys(channels).filter((c) => channels[c]),
      }, { headers: hdr(token) });
      setWaLinks(r.data.wa_links || []);
      toast.success(`Sent to ${r.data.recipient_count} recipient(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl border border-black/10 bg-white p-6">
      <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>Message users</div>
      <div className="text-xs text-neutral-500 mt-1">In-app, WhatsApp deep-links & email</div>
      <div className="mt-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest">Recipients</Label>
            <select data-testid="broadcast-target" value={target} onChange={(e) => setTarget(e.target.value)}
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="all">All users</option>
              <option value="customers">All Help Takers</option>
              <option value="assistants">All Earning Mates</option>
              <option value="user">One user (by id)</option>
            </select>
          </div>
          {target === "user" && (
            <div>
              <Label className="text-xs uppercase tracking-widest">User ID</Label>
              <Input data-testid="broadcast-userid" className="mt-1.5" value={userId} onChange={(e) => setUserId(e.target.value)} />
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Subject</Label>
          <Input data-testid="broadcast-subject" className="mt-1.5" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Message</Label>
          <Textarea data-testid="broadcast-body" rows={4} className="mt-1.5" value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
        <div className="flex gap-3 text-sm">
          {["inapp", "whatsapp", "email"].map((c) => (
            <label key={c} className="inline-flex items-center gap-1.5">
              <input type="checkbox" data-testid={`broadcast-ch-${c}`} checked={channels[c]}
                onChange={(e) => setChannels({ ...channels, [c]: e.target.checked })} /> {c}
            </label>
          ))}
        </div>
        <button data-testid="broadcast-send" onClick={send} disabled={busy} className="btn-primary inline-flex items-center gap-1.5">
          <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send"}
        </button>
      </div>
      {waLinks.length > 0 && (
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="font-bold text-sm text-emerald-900">WhatsApp deep-links ({waLinks.length}) — open to send</div>
          <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
            {waLinks.map((w) => (
              <a key={w.user_id} href={w.link} target="_blank" rel="noreferrer" className="block text-xs underline text-emerald-800 truncate">
                {w.name} · {w.phone}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Daily reminder settings ----------
export function AdminReminders({ token }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers: hdr(token) }).then((r) => setS(r.data.settings));
  }, [token]);
  if (!s) return <div className="text-neutral-500 text-sm p-4">Loading…</div>;
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/settings`, s, { headers: hdr(token) });
      setS(r.data.settings); toast.success("Saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-3xl border border-black/10 bg-white p-6">
      <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>Daily Mate reminder</div>
      <div className="text-xs text-neutral-500 mt-1">Auto-sent at your chosen time · pause anytime</div>
      <div className="mt-5 grid md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest">Enabled</Label>
          <div className="mt-1.5">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="reminder-enabled" checked={s.reminder_enabled}
                onChange={(e) => setS({ ...s, reminder_enabled: e.target.checked })} />
              {s.reminder_enabled ? "On" : "Paused"}
            </label>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Time (24h)</Label>
          <Input data-testid="reminder-time" type="time" className="mt-1.5" value={s.reminder_time || "08:00"}
            onChange={(e) => setS({ ...s, reminder_time: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Floating number</Label>
          <Input data-testid="floating-number" className="mt-1.5" value={s.floating_number || ""}
            onChange={(e) => setS({ ...s, floating_number: e.target.value })} />
        </div>
      </div>
      <div className="mt-4">
        <Label className="text-xs uppercase tracking-widest">Reminder message</Label>
        <Textarea data-testid="reminder-message" rows={3} className="mt-1.5" value={s.reminder_message}
          onChange={(e) => setS({ ...s, reminder_message: e.target.value })} />
      </div>
      <div className="mt-4">
        <Label className="text-xs uppercase tracking-widest">Admin alert email</Label>
        <Input data-testid="alert-email" className="mt-1.5" value={s.admin_alert_email || ""}
          onChange={(e) => setS({ ...s, admin_alert_email: e.target.value })} />
      </div>
      <button data-testid="settings-save" onClick={save} disabled={busy} className="btn-primary mt-5 inline-flex items-center gap-1.5">
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

// ---------- Task assignment ----------
export function AdminTasks({ token, mates = [] }) {
  const [form, setForm] = useState({ title: "", description: "", price_inr: 500, time_required_min: 60, location_address: "", assigned_to: [] });
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(false);
  const refresh = () => axios.get(`${API}/admin/tasks`, { headers: hdr(token) }).then((r) => setTasks(r.data.tasks || []));
  useEffect(() => { refresh(); }, [token]); // eslint-disable-line
  const toggle = (id) => setForm({ ...form, assigned_to: form.assigned_to.includes(id) ? form.assigned_to.filter(x => x !== id) : [...form.assigned_to, id] });
  const create = async () => {
    if (!form.title || !form.description || form.assigned_to.length === 0) { toast.error("Title, description & at least one mate required"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/tasks`, form, { headers: hdr(token) });
      setForm({ title: "", description: "", price_inr: 500, time_required_min: 60, location_address: "", assigned_to: [] });
      toast.success("Task created & assigned");
      refresh();
    } catch (e) { toast.error("Create failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-3xl border border-black/10 bg-white p-6">
        <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>Create task</div>
        <div className="mt-4 space-y-3">
          <Input data-testid="task-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Textarea data-testid="task-desc" rows={3} placeholder="Description / client requirement" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="task-price" type="number" placeholder="Price ₹" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: Number(e.target.value) })} />
            <Input data-testid="task-time" type="number" placeholder="Time (min)" value={form.time_required_min} onChange={(e) => setForm({ ...form, time_required_min: Number(e.target.value) })} />
          </div>
          <Input data-testid="task-loc" placeholder="Location / area" value={form.location_address} onChange={(e) => setForm({ ...form, location_address: e.target.value })} />
          <div>
            <div className="text-xs uppercase text-neutral-500 mb-1">Assign to Mates ({form.assigned_to.length})</div>
            <div className="max-h-40 overflow-y-auto border border-black/10 rounded-xl p-2 space-y-1">
              {mates.slice(0, 50).map((m) => (
                <label key={m.user_id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`task-assign-${m.user_id}`} checked={form.assigned_to.includes(m.user_id)} onChange={() => toggle(m.user_id)} />
                  <span>{m.bio?.slice(0, 40) || m.user_id.slice(0, 8)} · {m.city_slug}</span>
                </label>
              ))}
              {mates.length === 0 && <div className="text-xs text-neutral-500">No mates yet.</div>}
            </div>
          </div>
          <button data-testid="task-create" onClick={create} disabled={busy} className="btn-primary inline-flex items-center gap-1.5">
            <ListPlus className="w-4 h-4" /> {busy ? "Creating…" : "Create & assign"}
          </button>
        </div>
      </div>
      <div className="rounded-3xl border border-black/10 bg-white p-6">
        <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>Active tasks ({tasks.length})</div>
        <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto">
          {tasks.map((t) => {
            const responses = Object.entries(t.responses || {});
            return (
              <div key={t.id} className="rounded-xl border border-black/10 p-3 text-sm">
                <div className="flex justify-between"><b>{t.title}</b><span className="chip">{t.status}</span></div>
                <div className="text-neutral-500 text-xs mt-1">₹{t.price_inr} · {t.time_required_min} min · {t.location_address}</div>
                {responses.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1 text-[10px]">
                    {responses.map(([uid, r]) => (
                      <span key={uid} className={`chip ${r === "accepted" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}`}>
                        {uid.slice(0, 6)}: {r}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {tasks.length === 0 && <div className="text-xs text-neutral-500">No tasks yet.</div>}
        </div>
      </div>
    </div>
  );
}

// ---------- Onboarding QR (admin shows this to customer/mate on-site) ----------
export function AdminQrOnboard() {
  const url = `${window.location.origin}/register?ref=admin`;
  // Free QR API — encodes any url into a PNG
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(url)}&size=280x280&color=1D4ED8&bgcolor=ffffff`;
  const wa = `https://wa.me/?text=${encodeURIComponent(`Welcome to HeylpMate! Register in 30 sec: ${url}`)}`;
  const copy = () => { navigator.clipboard.writeText(url).then(() => toast.success("Link copied")); };
  return (
    <div className="rounded-3xl border border-black/10 bg-white p-6 md:p-8">
      <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>Onboard on the spot</div>
      <div className="text-xs text-neutral-500 mt-1">Show this QR to any customer or Mate — they scan → HeylpMate registration opens on their phone.</div>
      <div className="mt-6 grid md:grid-cols-2 gap-6 items-center">
        <div className="grid place-items-center">
          <img src={qrSrc} alt="HeylpMate onboarding QR" data-testid="onboard-qr-image" className="w-64 h-64 rounded-2xl border border-black/10" />
          <div className="mt-3 text-xs font-mono text-neutral-600 break-all text-center">{url}</div>
        </div>
        <div className="space-y-3">
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900">
            <b>How to use</b>
            <ul className="mt-2 list-disc list-inside space-y-1">
              <li>Meet a customer/helper in-person</li>
              <li>Show them this QR to scan with camera</li>
              <li>They register on HeylpMate in ~30 seconds</li>
            </ul>
          </div>
          <a href={wa} target="_blank" rel="noreferrer" data-testid="onboard-share-wa"
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-emerald-500 text-emerald-700 font-bold px-4 py-2 text-sm hover:bg-emerald-50">
            <QrIcon className="w-4 h-4" /> Share via WhatsApp
          </a>
          <button onClick={copy} data-testid="onboard-copy-link" className="ml-2 inline-flex items-center gap-1.5 rounded-full border-2 border-black/15 px-4 py-2 text-sm font-bold">
            <Copy className="w-4 h-4" /> Copy link
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Segmented users table ----------
export function AdminSegmentedUsers({ token }) {
  const [role, setRole] = useState("customer");
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [rows, setRows] = useState([]);
  const load = () => {
    const p = new URLSearchParams({ role });
    if (q) p.set("search", q);
    if (city) p.set("city", city);
    axios.get(`${API}/admin/users/segmented?${p}`, { headers: hdr(token) }).then((r) => setRows(r.data.users || []));
  };
  useEffect(() => { load(); }, [role, q, city]); // eslint-disable-line
  const exportCsv = () => {
    axios.get(`${API}/admin/users/export.csv?role=${role}`, { headers: hdr(token), responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a"); a.href = url; a.download = `heylpmate_${role}s.csv`; a.click();
      });
  };
  return (
    <div>
      <div className="flex gap-2 mb-3">
        {[["customer", "Help Takers"], ["assistant", "Earning Mates"]].map(([r, l]) => (
          <button key={r} data-testid={`user-role-${r}`} onClick={() => setRole(r)}
            className={`px-4 py-1.5 rounded-full text-sm font-bold ${role === r ? "text-white" : "border-2 border-black/10 bg-white"}`}
            style={role === r ? { background: r === "customer" ? "#00604A" : "#10B981" } : {}}>
            {l} ({role === r ? rows.length : ""})
          </button>
        ))}
        <div className="flex-1" />
        <Input data-testid="user-search" placeholder="Search name/mobile/email/city" className="max-w-xs" value={q} onChange={(e) => setQ(e.target.value)} />
        <Input data-testid="user-city" placeholder="City" className="max-w-[140px]" value={city} onChange={(e) => setCity(e.target.value)} />
        <button data-testid="user-export" onClick={exportCsv} className="btn-primary text-xs px-4 py-1.5 inline-flex items-center gap-1">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Mobile</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">City</th>
              <th className="p-3 text-left">Location</th>
              <th className="p-3 text-left">Registered</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u, i) => (
              <tr key={u.id} className={`border-t border-black/5 ${i % 2 ? "bg-neutral-50/40" : ""}`}>
                <td className="p-3">{u.name}</td>
                <td className="p-3 font-bold">{u.phone || <span className="text-red-500">missing</span>}</td>
                <td className="p-3 text-neutral-500">{u.email}</td>
                <td className="p-3">{u.city || "—"}</td>
                <td className="p-3 text-xs text-neutral-500 truncate max-w-[240px]">{u.location?.address || (u.location?.lat ? `${u.location.lat.toFixed(3)}, ${u.location.lng.toFixed(3)}` : "—")}</td>
                <td className="p-3 text-xs text-neutral-500">{(u.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-neutral-500 text-sm">No users match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
